import { useState } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { AncreActions } from '../ui/ActionsDOnglet.js'
import { ItemListPage } from './ItemListPage.js'
import { CategoryTreePage } from './CategoryTreePage.js'
import { SupplierListPage } from './SupplierListPage.js'
import { TaxListPage } from './TaxListPage.js'

/**
 * Catalogue en onglets.
 *
 * Articles, catégories et fournisseurs décrivent la même chose — ce que la
 * boutique vend et d'où ça vient. Les éparpiller dans la barre latérale
 * obligeait à en sortir pour rattacher un article à un fournisseur ; ils sont
 * désormais à un onglet l'un de l'autre.
 *
 * ⚠️ Chaque onglet a sa propre URL plutôt qu'un état interne : une vue reste
 * ainsi partageable, rechargeable et navigable au bouton Précédent — la même
 * règle que pour les filtres (§7.9).
 */

export type OngletCatalogue = 'articles' | 'categories' | 'fournisseurs' | 'tva'

const CHEMINS: Record<OngletCatalogue, string> = {
  articles: '',
  categories: '/categories',
  fournisseurs: '/fournisseurs',
  tva: '/tva',
}

export function CataloguePage({ onglet }: { onglet: OngletCatalogue }): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  /**
   * ⚠️ Un ÉTAT, pas une `ref`. Le nœud n'existe pas au premier rendu : avec une
   * `ref`, l'onglet ne se redessinerait jamais et le portail n'aurait nulle
   * part où écrire. Le poser dans l'état provoque le second rendu qu'il faut.
   */
  const [ancre, setAncre] = useState<HTMLDivElement | null>(null)

  return (
    <Box>
      {/* ⭐ Onglets et actions sur LA MÊME LIGNE. Il y avait deux bandeaux :
          les onglets, puis « Catalogue / 18 articles » avec les boutons — un
          titre qui répétait l'onglet actif, et la première donnée repoussée
          d'une centaine de pixels. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          mb: 4,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={onglet}
          onChange={(_, v: OngletCatalogue) => navigate(`/s/${slug}/catalogue${CHEMINS[v]}`)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Tab value="articles" label="Produits & services" />
          <Tab value="categories" label="Catégories" />
          <Tab value="fournisseurs" label="Fournisseurs" />
          <Tab value="tva" label="TVA" />
        </Tabs>
        <Box
          ref={setAncre}
          sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, pb: 1 }}
        />
      </Box>

      <AncreActions.Provider value={ancre}>
        {onglet === 'articles' && <ItemListPage />}
        {onglet === 'categories' && <CategoryTreePage />}
        {onglet === 'fournisseurs' && <SupplierListPage />}
        {onglet === 'tva' && <TaxListPage />}
      </AncreActions.Provider>
    </Box>
  )
}
